import { useState, useEffect } from "react";
import moment from "moment";
import { fetchWrapper } from "src/helpers";
import type { VolumeCompareSnapshot } from "src/common/charts/volumeCompareChart.types";
import {
  normalizeVolumeCompareSnapshot,
  recalculateVolumeSnapshotWithNewVolume,
} from "src/common/charts/volumeCompareChart.utils";
import { useSocket } from "src/components/SocketContext";
import { LtpMessage } from "src/helpers";

export function useVolumeAnalysis(
  symbol: string | undefined,
  date: string | undefined,
  token: string | undefined,
  pause: boolean = false,
) {
  const [volumeCompareSnapshot, setVolumeCompareSnapshot] =
    useState<VolumeCompareSnapshot | null>(null);
  const [loadingVolume, setLoadingVolume] = useState(true);
  const [dataSymbolVolume, setDataSymbolVolume] = useState<string | null>(null);
  const { on, off, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected || !symbol) return;

    const handleStockUpdate = (messages: LtpMessage[]) => {
      const update = messages.find((m) => m.symbol_name === symbol);
      const isToday = moment().tz("Asia/Kolkata").format("YYYY-MM-DD");
      if (update && update.volume && date === isToday) {
        const newVolume = parseFloat(update.volume);
        if (!isNaN(newVolume)) {
          setVolumeCompareSnapshot((prev) => {
            if (!prev) return prev;
            return recalculateVolumeSnapshotWithNewVolume(prev, newVolume);
          });
        }
      }
    };

    on("stock_update", handleStockUpdate);
    return () => {
      off("stock_update", handleStockUpdate);
    };
  }, [isConnected, symbol, on, off, date]);

  useEffect(() => {
    if (pause) return;
    if (!symbol || !token || !date) {
      setVolumeCompareSnapshot(null);
      setLoadingVolume(false);
      setDataSymbolVolume(null);
      return;
    }

    const fetchVolumeAnalysis = async (signal: AbortSignal) => {
      setLoadingVolume(true);
      try {
        const response = await fetchWrapper.get(
          `/api/dashboard/volume_analysis?symbol=${encodeURIComponent(
            symbol,
          )}&date=${date}`,
          undefined,
          { Authorization: `Bearer ${token}` },
          signal,
        );

        if (signal.aborted) return;

        if (response.success && response.data) {
          setVolumeCompareSnapshot(
            normalizeVolumeCompareSnapshot(response.data),
          );
        } else {
          setVolumeCompareSnapshot(null);
        }
        setDataSymbolVolume(symbol);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") return;
        console.error("Error fetching volume analysis:", error);
        setVolumeCompareSnapshot(null);
        setDataSymbolVolume(symbol);
      } finally {
        if (!signal.aborted) setLoadingVolume(false);
      }
    };

    const controller = new AbortController();
    fetchVolumeAnalysis(controller.signal);
    return () => controller.abort();
  }, [symbol, date, token, pause]);

  const isLoading =
    loadingVolume || pause || (!!symbol && dataSymbolVolume !== symbol);

  return { volumeCompareSnapshot, loadingVolume: isLoading };
}
